import type { UserProfile } from './AuthProvider.js';

export type AppCapability =
  | 'food.create'
  | 'document.import.review'
  | 'personnel.manage'
  | 'shared_tablet.manage'
  | 'tenant.features.manage'
  /**
   * Recovery of a zero/closed historical lot can change the operational
   * stock view while preserving the original document.  It is intentionally
   * not delegated to tenant roles: only the platform-owner consultant may
   * initiate it, and the database repeats the same gate.
   */
  | 'warehouse.historical_lots';

const ROLE_CAPABILITIES: Record<string, ReadonlySet<AppCapability>> = {
  consulente: new Set([
    'food.create',
    'document.import.review',
    'personnel.manage',
    'shared_tablet.manage',
    'tenant.features.manage',
  ]),
  ristoratore: new Set(['food.create', 'document.import.review', 'personnel.manage']),
  osa: new Set(['food.create', 'document.import.review']),
  staff: new Set(),
};

export function hasAppCapability(
  profile: Pick<UserProfile, 'role' | 'is_platform_owner'> | null | undefined,
  capability: AppCapability,
): boolean {
  if (profile?.is_platform_owner === true) return true;
  return !!profile?.role && ROLE_CAPABILITIES[profile.role]?.has(capability) === true;
}

export function canCreateFood(
  profile: Pick<UserProfile, 'role' | 'is_platform_owner'> | null | undefined,
): boolean {
  return hasAppCapability(profile, 'food.create');
}

/**
 * La creazione di un alimento nata dentro il flusso Etichette è operativa e
 * viene marcata `creation_source=quick_label` fino al server. Non concede allo
 * staff la creazione generica dalle anagrafiche o dal magazzino.
 */
export function canCreateQuickLabelFood(
  profile: Pick<UserProfile, 'role' | 'is_platform_owner'> | null | undefined,
): boolean {
  return canCreateFood(profile) || profile?.role === 'staff';
}

export function canManagePersonnel(
  profile: Pick<UserProfile, 'role' | 'is_platform_owner'> | null | undefined,
): boolean {
  return hasAppCapability(profile, 'personnel.manage');
}

/**
 * Tenant feature switches require a current tenant membership. The platform
 * owner keeps the role override only after the active membership has resolved;
 * a missing role therefore fails closed in the UI as it does in the RPC.
 */
export function canManageTenantFeatures(
  profile: Pick<UserProfile, 'role' | 'is_platform_owner'> | null | undefined,
): boolean {
  if (!profile?.role) return false;
  return hasAppCapability(profile, 'tenant.features.manage');
}

/**
 * Il configuratore commerciale è control-plane del consulente, non una
 * funzione del locale attivo. Il ruolo globale resta quindi autorevole anche
 * quando il consulente opera dentro un tenant con membership ristoratore.
 */
export function canConfigureCommercialPricing(
  profile: Pick<UserProfile, 'role' | 'global_role' | 'is_platform_owner'> | null | undefined,
): boolean {
  return profile?.is_platform_owner === true
    || profile?.global_role === 'consulente'
    || profile?.role === 'consulente';
}
